import { ViewData } from "@wizeoo/akm-common-lib";
import { ApplicationRef, Injectable } from "@angular/core";
import { SwUpdate } from "@angular/service-worker";
import { CONFIRMATION_MODE, CONFIRMATION_RESULT } from '@wizeoo/akm-core-lib';
import { ConfirmationResultObj, WizPopUp, WizTranslationManager } from "@wizeoo/akm-component-lib";
import { concat, interval } from 'rxjs';
import { filter, first } from 'rxjs/operators';
import { cloneDeep } from 'lodash';

/**
 * interface pour le json contenu dans la propriété appData du fichier de config
 * du Service Worker (ngsw-config.json)
 */
export interface AppData {
    version: string;
    changelog: string;
}

/**
 * Type d'événement que le service de gestion de mise à jour peut recevoir
 */
export enum SW_UPDATE_EVENT_TYPE {
    DETECTED = 'VERSION_DETECTED',
    READY = 'VERSION_READY',
    FAILED = 'VERSION_INSTALLATION_FAILED'
}

/**
 * Service de gestion de la mise à jour du Service Worker
 */
@Injectable()
export class SwUpdateService {

    public static checkUpdateInterval: number = 60 * 60 * 1000; // Toutes les heures

    constructor(private appRef: ApplicationRef, private swUpdate: SwUpdate) {
        if (!swUpdate.isEnabled) {
            // console.log('Service Worker is disabled');
            return;
        }

        // On souscrit aux événements qui peuvent se produire concernant les mises à jour
        swUpdate.versionUpdates.subscribe(evt => {
            switch (evt.type) {
                case SW_UPDATE_EVENT_TYPE.DETECTED : {
                    const appData:AppData = cloneDeep(evt.version.appData);
                    console.log(`Detected new app version: ${appData.version}`);
                    break;
                }
                case SW_UPDATE_EVENT_TYPE.READY : {
                    const currentAppData:AppData = cloneDeep(evt.currentVersion.appData);
                    const latestAppData:AppData = cloneDeep(evt.latestVersion.appData);
                    console.log(`Current app version: ${currentAppData.version}`);
                    console.log(`Latest app version ready for use: ${latestAppData.version}`);
                    
                    // Une mise à jour est prête
                    // On ne déclenche la popup que si on est sur que la vue root est UP
                    // Sinon on attend que la vue root soit UP
                    if (ViewData.getInstance().isRootViewInitComplete()) {
                        this.showUpdatePopup(latestAppData, swUpdate);
                    } else {
                        ViewData.getInstance().rootViewInitComplete.pipe(filter(value => value)).subscribe(value => {   
                            // swUpdate.activateUpdate().then(() => location.reload()); // maj automatique, pas de confirmation
                            this.showUpdatePopup(latestAppData, swUpdate);
                        });
                    }

                    break;
                }
                case SW_UPDATE_EVENT_TYPE.FAILED : {
                    const appData = cloneDeep(evt.version.appData);
                    console.log(`Failed to install app version '${appData.version}': ${evt.error}`);
                    break;
                }
            }
        });

        // On vérifie la présence d'une mise à jour dés que l'application est stable
        // puis réguliérement selon un intervalle donné
        const appIsStable = appRef.isStable.pipe(first(isStable => isStable === true));
        const everyInterval = interval(SwUpdateService.checkUpdateInterval);
        const everyIntervalOnceAppIsStable = concat(appIsStable, everyInterval);
        everyIntervalOnceAppIsStable.subscribe(() => {
            swUpdate.checkForUpdate();
            //.then((value) => {
            //    console.log(`Check Update is ${value}`);
            //});
        });
    }

    // Affichage de la popup signalant une mise à jour
    private showUpdatePopup(latestAppData: AppData, swUpdate: SwUpdate) {
        WizPopUp.confirm(
            WizTranslationManager.getInstance().translateKey('$.app.update.title.$', [latestAppData.version,]),
            '$.app.update.detail.$',
            this.updateVersion.bind(this),
            CONFIRMATION_MODE.CONFIRM_CANCEL,
            swUpdate
        ).show();
    }

    // Mise à jour de l'application et rechargement de la page
    private updateVersion(result: ConfirmationResultObj): void {
        if (result.result === CONFIRMATION_RESULT.CONFIRM) {
            result.data.activateUpdate().then(() => location.reload()); 
        } 
    }

}